import { useEffect, useState } from 'react';
import { MultiChipSelect } from './MultiChipSelect';
import {
  TARGET_TYPES, csvToArr, arrToCsv, resolveLabels,
} from './campaignUtils';
import { RELATION_CONFIGS } from '../relationConfigs';
import { CustomerDynamicGroupQuickCreateModal } from './CustomerDynamicGroupQuickCreateModal';

// Hedef türüne göre datagrid config'i (ALL'da seçim yok).
const CONFIGS = {
  CUSTOMER: RELATION_CONFIGS.customer,
  GROUP: RELATION_CONFIGS.customerGroup,
  SUBGROUP: RELATION_CONFIGS.customerSubGroup,
  DYNAMIC: RELATION_CONFIGS.customerDynamicGroup,
};

// CSV <-> chip item köprüsü; etiketleri verilen tanım endpoint'inden çözer.
function useCsvItems(endpoint, csv, setCsv) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const ids = csvToArr(csv);
    if (!ids.length) { setItems([]); return; }
    let alive = true;
    resolveLabels(endpoint, ids).then((map) => {
      if (alive) setItems(ids.map((id) => ({ value: id, label: map[id] || id })));
    }).catch(() => { if (alive) setItems(ids.map((id) => ({ value: id, label: id }))); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csv, endpoint]);
  const onChange = (next) => { setItems(next); setCsv(arrToCsv(next.map((x) => x.value))); };
  return [items, onChange];
}

export function CampaignCustomersTab({ form, setForm, isEdit }) {
  const set = (patch) => setForm((c) => ({ ...c, ...patch }));
  const targetType = form.customerTargetType || 'ALL';
  const activeMeta = TARGET_TYPES.find((t) => t.value === targetType) || TARGET_TYPES[0];

  // Aktif kapsam — seçili türün csvField'ine bağlı (ALL'da csvField yok → boş).
  const [scopeItems, setScopeItems] = useCsvItems(
    activeMeta.endpoint || '/api/customers',
    activeMeta.csvField ? (form[activeMeta.csvField] || '') : '',
    (v) => { if (activeMeta.csvField) set({ [activeMeta.csvField]: v }); },
  );
  const [exclItems, setExclItems] = useCsvItems('/api/customers', form.excludedCustomerIdsCsv, (v) => set({ excludedCustomerIdsCsv: v }));

  // Kampanya ekranından ayrılmadan yeni Müşteri Özelleştirilmiş Grup oluşturma (#242).
  const [dynGroupModal, setDynGroupModal] = useState(false);
  const handleDynGroupCreated = (item) => {
    // Yeni grup zaten DYNAMIC kapsam CSV'sine eklenir; hedef türü DYNAMIC değilse ona geç.
    if (targetType !== 'DYNAMIC') {
      const clear = {};
      TARGET_TYPES.forEach((t) => { if (t.csvField && t.value !== 'DYNAMIC') clear[t.csvField] = ''; });
      set({ customerTargetType: 'DYNAMIC', ...clear, targetCustomerDynamicGroupIdsCsv: item.value });
    } else if (!scopeItems.some((x) => x.value === item.value)) {
      setScopeItems([...scopeItems, item]);
    }
    setDynGroupModal(false);
  };

  const switchType = (next) => {
    if (!isEdit || next === targetType) return;
    // Tek kapsam aktif olsun: diğer hedef CSV'lerini temizle.
    const clear = {};
    TARGET_TYPES.forEach((t) => { if (t.csvField && t.value !== next) clear[t.csvField] = ''; });
    set({ customerTargetType: next, ...clear });
  };

  const isGroupLike = targetType === 'GROUP' || targetType === 'SUBGROUP' || targetType === 'DYNAMIC';
  const unitWord = targetType === 'ALL' ? '' : isGroupLike ? 'grup' : 'müşteri';
  const selCount = targetType === 'ALL' ? '∞' : scopeItems.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <h3 className="cmp-section-title">🎯 Müşteri Hedeflemesi</h3>
        <p className="cmp-section-sub">Bu promosyonun uygulanacağı müşteri hedefleri.</p>
      </div>

      {/* 1) Hedef türü kartları — auto-fill ile kartlar sabit boyutta (Ürün Hedefi sekmesiyle aynı standart). */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
        {TARGET_TYPES.map((t) => {
          const active = targetType === t.value;
          return (
            <div
              key={t.value}
              role="button"
              onClick={() => switchType(t.value)}
              style={{ ...typeCard, ...(active ? typeCardActive : null), cursor: isEdit ? 'pointer' : 'default' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }} aria-hidden="true">{t.icon}</span>
                <span style={{ fontWeight: 800, fontSize: 14 }}>{t.label}</span>
                {active && <span style={{ marginLeft: 'auto', color: 'var(--accent)', fontWeight: 800 }}>✓</span>}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-secondary,#6b7280)', marginTop: 6 }}>{t.desc}</div>
            </div>
          );
        })}
      </div>

      {/* 2) Seçilen türe göre çoklu seçim listesi */}
      <div style={panel}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <h5 style={{ ...panelTitle, margin: 0 }}>{targetType === 'ALL' ? 'Kapsam' : `${activeMeta.label} Listesi`}</h5>
          {targetType === 'DYNAMIC' && isEdit && (
            <button type="button" onClick={() => setDynGroupModal(true)} style={newGroupBtn}>
              + Yeni Özel Grup
            </button>
          )}
        </div>
        {targetType === 'ALL' ? (
          <div style={{ padding: 18, color: 'var(--text-secondary,#6b7280)', fontSize: 13 }}>
            Tüm müşteriler için geçerli — ek seçim gerekmez.
          </div>
        ) : (
          <MultiChipSelect
            key={targetType}
            items={scopeItems}
            onChange={setScopeItems}
            config={CONFIGS[targetType]}
            placeholder={`${activeMeta.label} ara ve ekle…`}
            disabled={!isEdit}
          />
        )}
      </div>

      {/* 3) Özet & Hariç Tutulanlar */}
      <div style={panel}>
        <h5 style={panelTitle}>📋 Özet & Hariç Tutulanlar</h5>
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 14 }}>
          <div>
            <div style={summaryLabel}>Hedef Türü</div>
            <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, background: '#15803d', color: '#fff', marginTop: 4 }}>{activeMeta.label}</span>
          </div>
          <div>
            <div style={summaryLabel}>Seçim</div>
            <div style={{ fontWeight: 700, marginTop: 6 }}>{selCount} {unitWord}</div>
          </div>
          <div>
            <div style={summaryLabel}>Hariç Tutulan</div>
            <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, background: '#fee2e2', color: '#b91c1c', marginTop: 4 }}>{exclItems.length} müşteri</span>
          </div>
        </div>
        <div style={summaryLabel}>Hariç tutulacak müşteriler</div>
        <div style={{ marginTop: 6 }}>
          <MultiChipSelect items={exclItems} onChange={setExclItems} config={RELATION_CONFIGS.customer} placeholder="Hariç tutulacak müşteri ara ve ekle…" disabled={!isEdit} />
        </div>
      </div>

      {dynGroupModal && (
        <CustomerDynamicGroupQuickCreateModal
          onClose={() => setDynGroupModal(false)}
          onCreated={handleDynGroupCreated}
        />
      )}
    </div>
  );
}

const typeCard = {
  border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 14, padding: '12px 14px',
  background: 'var(--bg-card,#fff)', transition: 'all 120ms',
};
const typeCardActive = {
  borderColor: 'var(--accent)', background: 'rgba(0,115,234,0.06)', boxShadow: '0 4px 14px rgba(0,115,234,0.12)',
};
const panel = {
  border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 16, padding: 18, background: 'var(--bg-card,#fff)',
};
const panelTitle = { margin: '0 0 12px', fontSize: 14, fontWeight: 800 };
const summaryLabel = { fontSize: 12, color: 'var(--text-secondary,#6b7280)', fontWeight: 600 };
const newGroupBtn = {
  marginLeft: 'auto', border: '1px solid var(--accent)', color: 'var(--accent)', background: 'rgba(0,115,234,0.06)',
  borderRadius: 999, padding: '5px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
};

export default CampaignCustomersTab;
